import * as THREE from 'three'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'

// ---------- Base ----------
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

// Responsive flags
let isMobile = window.matchMedia('(max-width: 768px)').matches

// Text & material
const textureLoader = new THREE.TextureLoader()
const matcapTexture = textureLoader.load('/textures/matcaps/3.png')
matcapTexture.colorSpace = THREE.SRGBColorSpace
const material = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })

// Sizes / renderer
const sizes = { width: window.innerWidth, height: window.innerHeight }
const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.75 : 2))

// Camera
const camera = new THREE.PerspectiveCamera(isMobile ? 70 : 60, sizes.width / sizes.height, 0.1, 100)
scene.add(camera)

// ---------- Post-processing: radial edge blur (fake DOF) ----------
// Separable Gaussian whose radius ramps from 0 at screen centre to uMaxBlur at
// the corners (see glowing-orb/docs/radial-edge-blur.md). The 3D text lives on
// layer 1 so it skips this chain and is drawn crisp on top afterwards.
const RadialBlurShader = {
  uniforms: {
    tDiffuse: { value: null },
    uResolution: { value: new THREE.Vector2() },
    uDirection: { value: new THREE.Vector2(1, 0) },
    uMaxBlur: { value: 2.5 },
    uStart: { value: 0.45 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uResolution;
    uniform vec2 uDirection;
    uniform float uMaxBlur;
    uniform float uStart;
    varying vec2 vUv;

    void main() {
      vec2 c = vUv - 0.5;
      float dist = length(c) / 0.7071068;
      float edge = smoothstep(uStart, 1.0, dist);
      float radius = edge * uMaxBlur;

      vec2 texel = uDirection / uResolution;
      // Early-out: crisp centre region is a single fetch per pass.
      if (radius * 4.0 < 0.5) {
        gl_FragColor = texture2D(tDiffuse, vUv);
        return;
      }

      // 9-tap Gaussian (weights sum to 1).
      vec4 sum = texture2D(tDiffuse, vUv) * 0.227027;
      vec2 o1 = texel * radius;
      vec2 o2 = texel * radius * 2.0;
      vec2 o3 = texel * radius * 3.0;
      vec2 o4 = texel * radius * 4.0;
      sum += texture2D(tDiffuse, vUv + o1) * 0.1945946;
      sum += texture2D(tDiffuse, vUv - o1) * 0.1945946;
      sum += texture2D(tDiffuse, vUv + o2) * 0.1216216;
      sum += texture2D(tDiffuse, vUv - o2) * 0.1216216;
      sum += texture2D(tDiffuse, vUv + o3) * 0.054054;
      sum += texture2D(tDiffuse, vUv - o3) * 0.054054;
      sum += texture2D(tDiffuse, vUv + o4) * 0.016216;
      sum += texture2D(tDiffuse, vUv - o4) * 0.016216;
      gl_FragColor = sum;
    }
  `,
}

const composer = new EffectComposer(renderer)
composer.setPixelRatio(renderer.getPixelRatio())
composer.addPass(new RenderPass(scene, camera))

const blurH = new ShaderPass(RadialBlurShader)
blurH.uniforms.uDirection.value.set(1, 0)
composer.addPass(blurH)

const blurV = new ShaderPass(RadialBlurShader)
blurV.uniforms.uDirection.value.set(0, 1)
composer.addPass(blurV)

composer.addPass(new OutputPass())

function syncComposerSize() {
  composer.setPixelRatio(renderer.getPixelRatio())
  composer.setSize(sizes.width, sizes.height)
  const pr = renderer.getPixelRatio()
  for (const p of [blurH, blurV]) p.uniforms.uResolution.value.set(sizes.width * pr, sizes.height * pr)
}
syncComposerSize()

// Orbit-ish params (works for mouse & touch)
let angleX = 0, angleY = 0
let targetX = 0, targetY = 0
let radius = isMobile ? 4.5 : 3
let targetZoom = radius
const easing = 0.12
const maxZoom = isMobile ? 3.2 : 2.8        // closer
const minZoom = isMobile ? 7.5 : 6          // farther
const moveSpeed = isMobile ? 1.6 : 2

// ---------- Text ----------
let text
const fontLoader = new FontLoader()
const TEXT_SIZE = isMobile ? 0.18 : 0.25
const TEXT_DEPTH = isMobile ? 0.12 : 0.2

fontLoader.load('/fonts/helvetiker_regular.typeface.json', (font) => {
  const textGeometry = new TextGeometry('IMAGINING NEW REALITIES', {
    font, size: TEXT_SIZE, depth: TEXT_DEPTH,
    curveSegments: 5,
    bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.02, bevelOffset: 0, bevelSegments: 4
  })
  textGeometry.center()
  text = new THREE.Mesh(textGeometry, material)
  // Layer 1: excluded from the blurred composer pass, drawn crisp on top.
  text.layers.set(1)
  scene.add(text)
})

// ---------- Octahedron swarm (instanced) ----------
const OCT_MAX = 750
const octGeo = new THREE.OctahedronGeometry(0.075, 0)
const octInstanced = new THREE.InstancedMesh(octGeo, material, OCT_MAX)
octInstanced.frustumCulled = false
octInstanced.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
scene.add(octInstanced)

// Responsive density/spread
const SPREAD = {
  x: isMobile ? 5 : 8,
  y: isMobile ? 20 : 15,
  z: isMobile ? 5 : 7
}

// ---- NEW: center-biased Y sampler (more in middle) ----
const BIAS_SAMPLES = isMobile ? 3 : 5 // higher = stronger center bias
function centerBiasedY(spread, samples = BIAS_SAMPLES) {
  // Irwin–Hall: sum of uniforms -> peaked at 0 after normalization
  let s = 0
  for (let i = 0; i < samples; i++) s += Math.random()
  const r = (s / samples) * 2 - 1 // [-1,1], concentrated near 0
  return r * spread
}

// Keep-out box around the centered 3D text so gems never spawn inside it and
// clip. Extents cover the text's size plus its breathe motion (±0.1), the
// gems' own float amplitude (±~0.37) and max gem radius (~0.25).
const TEXT_CLEAR = {
  x: TEXT_SIZE * 23 * 0.35 + 0.5, // ~half the text width (23 chars) + margin
  y: TEXT_SIZE / 2 + 0.85,
  z: TEXT_DEPTH / 2 + 0.7
}
// Keep-out tunnel between the default camera position (+z axis, out to the
// farthest zoom) and the text, matching the text's silhouette, so a gem can't
// spawn in the line of sight and block the headline.
const VIEW_CLEAR_DEPTH = minZoom + 1
const insideTextBox = (v) =>
  Math.abs(v.x) < TEXT_CLEAR.x && Math.abs(v.y) < TEXT_CLEAR.y && Math.abs(v.z) < TEXT_CLEAR.z
const insideViewTunnel = (v) =>
  v.z > 0 && v.z < VIEW_CLEAR_DEPTH && Math.abs(v.x) < TEXT_CLEAR.x && Math.abs(v.y) < TEXT_CLEAR.y
function spawnBase() {
  const v = new THREE.Vector3()
  do {
    v.set(
      (Math.random() - 0.5) * (SPREAD.x * 2),
      centerBiasedY(SPREAD.y),                         // <-- biased toward center vertically
      (Math.random() - 0.5) * (SPREAD.z * 2)
    )
  } while (insideTextBox(v) || insideViewTunnel(v))
  return v
}

const octData = Array.from({ length: OCT_MAX }, () => ({
  base: spawnBase(),
  rotAxis: new THREE.Vector3(Math.random(), Math.random(), Math.random()).normalize(),
  scale: isMobile ? 0.6 + Math.random() * 2 : 0.9 + Math.random() * 2.3, // larger on laptops/desktops
  phase: Math.random() * Math.PI * 2,
  speed: 0.3 + Math.random() * 0.7,
  floatAmp: 0.12 + Math.random() * 0.25,
  rotSpeed: 0.5 + Math.random()
}))

const _mat = new THREE.Matrix4()
const _pos = new THREE.Vector3()
const _quat = new THREE.Quaternion()
const _scl = new THREE.Vector3(1, 1, 1)

// Adaptive count by viewport area & DPR
function computeVisibleOcts() {
  const area = sizes.width * sizes.height
  const dpr  = Math.min(window.devicePixelRatio, isMobile ? 1.75 : 2)
  const density = isMobile ? 0.0002 : 0.00024 // bump mobile density; keep desktop the same
  // Never request more instances than we actually allocated to avoid crashes on large screens.
  const targetCount = Math.round(area * dpr * density)
  const minCount = isMobile ? 500 : 500
  return THREE.MathUtils.clamp(targetCount, minCount, OCT_MAX)
}

let visibleOcts = 0

// Seed initial matrices
for (let i = 0; i < OCT_MAX; i++) {
  const d = octData[i]
  _pos.copy(d.base); _quat.setFromAxisAngle(d.rotAxis, 0); _scl.setScalar(d.scale)
  _mat.compose(_pos, _quat, _scl)
  octInstanced.setMatrixAt(i, _mat)
}
visibleOcts = computeVisibleOcts()
octInstanced.count = visibleOcts
octInstanced.instanceMatrix.needsUpdate = true

// ---------- Input: mouse, wheel, touch ----------
function setTargetsFromNormalized(nx, ny) {
  const distanceFromCenter = Math.hypot(nx, ny)
  targetZoom = maxZoom + (minZoom - maxZoom) * THREE.MathUtils.clamp(distanceFromCenter, 0, 1)
  targetX = -nx * Math.PI * 0.75
  targetY = ny * moveSpeed
}

document.addEventListener('mousemove', (e) => {
  const nx = e.clientX / sizes.width - 0.5
  const ny = e.clientY / sizes.height - 0.5
  setTargetsFromNormalized(nx, ny)
})

let lastTouchDist = null
document.addEventListener('touchstart', (e) => {
  if (e.touches.length === 2) {
    const dx = e.touches[0].clientX - e.touches[1].clientX
    const dy = e.touches[0].clientY - e.touches[1].clientY
    lastTouchDist = Math.hypot(dx, dy)
  }
}, { passive: true })

document.addEventListener('touchmove', (e) => {
  if (e.touches.length === 1) {
    const t = e.touches[0]
    const nx = t.clientX / sizes.width - 0.5
    const ny = t.clientY / sizes.height - 0.5
    setTargetsFromNormalized(nx, ny)
  }
}, { passive: false })

document.addEventListener('touchend', () => { lastTouchDist = null }, { passive: true })

// ---------- Resize ----------
window.addEventListener('resize', () => {
  isMobile = window.matchMedia('(max-width: 768px)').matches
  sizes.width = window.innerWidth
  sizes.height = window.innerHeight

  camera.aspect = sizes.width / sizes.height
  camera.fov = isMobile ? 70 : 60
  camera.updateProjectionMatrix()

  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.75 : 2))
  syncComposerSize()

  // (Optional) you could regenerate octData with new SPREAD here if you want bias to adapt.
  visibleOcts = computeVisibleOcts()
  octInstanced.count = visibleOcts
  octInstanced.instanceMatrix.needsUpdate = true
})

// ---------- Animate ----------
const depthOnlyMaterial = new THREE.MeshBasicMaterial({ colorWrite: false })
const clock = new THREE.Clock()
function tick() {
  const tGlobal = clock.getElapsedTime()

  // Eased orbit + zoom
  angleX += (targetX - angleX) * easing
  angleY += (targetY - angleY) * easing
  radius += (targetZoom - radius) * easing

  camera.position.x = radius * Math.cos(angleY) * Math.sin(angleX)
  camera.position.y = angleY
  camera.position.z = radius * Math.cos(angleY) * Math.cos(angleX)
  camera.lookAt(0, 0, 0)

  // Text “breathe”
  if (text) {
    const amp = 0.1, spd = 2
    const s = Math.sin(performance.now() * 0.00075 * spd) * amp
    text.position.y = s
    text.rotation.z = s
  }

  // Octahedrons float/rotate
  for (let i = 0; i < octInstanced.count; i++) {
    const d = octData[i]
    const tt = tGlobal * d.speed + d.phase
    _pos.copy(d.base); _pos.y += Math.sin(tt) * d.floatAmp
    _quat.setFromAxisAngle(d.rotAxis, tt * d.rotSpeed)
    _scl.setScalar(d.scale)
    _mat.compose(_pos, _quat, _scl)
    octInstanced.setMatrixAt(i, _mat)
  }
  octInstanced.instanceMatrix.needsUpdate = true

  // Blurred pass: camera sees layer 0 only (gems), text excluded.
  camera.layers.set(0)
  composer.render()

  // Depth prepass: redraw the gems into the depth buffer only (no color), so
  // the crisp text pass keeps the original scene occlusion — gems that pass
  // in front of the text still hide it.
  renderer.autoClear = false
  renderer.clearDepth()
  scene.overrideMaterial = depthOnlyMaterial
  renderer.render(scene, camera)
  scene.overrideMaterial = null

  // Crisp overlay pass: draw the layer-1 text, depth-tested against the gems.
  camera.layers.set(1)
  renderer.render(scene, camera)
  renderer.autoClear = true
  camera.layers.set(0)

  requestAnimationFrame(tick)
}
tick()
